import React from 'react'
import { Form, Input, Button, Icon, Row, Col, Table, DatePicker, Select, Tooltip, Modal, message } from 'antd'
import NumberFormat from 'react-number-format'
import moment from 'moment'
import API from '../../services/API'
import InvoiceDetailModal from './components/InvoiceDetailModal'

const { RangePicker } = DatePicker
// eslint-disable-next-line prefer-destructuring
const Option = Select.Option

const INCLUDES = 'include[]=payments&include[]=items.product&include[]=customer.customer_type&include[]=user&include[]=subsidiary'

class InvoicesListContainer extends React.Component {
    state = {
        loading: false,
        invoices: [],
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
        filters: {
            unique_id: '',
            rfc: '',
            socialReason: '',
            subsidiary_id: null,
        },
        dateRange: {
            startDate: null,
            endDate: null,
        },
        subsidiaries: {
            data: [],
            loading: false,
        },
        selectedInvoice: null,
        isDetailModalOpen: false,
        resend: {
            visible: false,
            order: null,
            email: '',
            sending: false,
        },
    }

    componentDidMount() {
        this.getSubsidiaries()
        this.getData({ page: 1 })
    }

    getSubsidiaries = async () => {
        this.setState({ subsidiaries: { data: [], loading: true } })

        await API.get('/api/subsidiaries?pagination=false&filter[active]=1')
            .then(response => {
                this.setState({ subsidiaries: { data: response.data, loading: false } })
            })
            .catch(err => {
                console.error('Error al obtener sucursales: ', err)
                this.setState({ subsidiaries: { data: [], loading: false } })
            })
    }

    getData = async ({ page = 1 } = {}) => {
        const { filters, dateRange } = this.state

        let query = `per_page=10&page=${page}&${INCLUDES}`

        if (filters.unique_id) {
            query += `&filter[unique_id]=${filters.unique_id}`
        }

        if (filters.subsidiary_id) {
            query += `&filter[subsidiary_id]=${filters.subsidiary_id}`
        }

        if (filters.rfc) {
            query += `&filter[customer.rfc]=${filters.rfc}`
        }

        if (filters.socialReason) {
            query += `&filter[customer.social_reason]=${filters.socialReason}`
        }

        if (dateRange.startDate && dateRange.endDate) {
            const start = dateRange.startDate.format('YYYY-MM-DD 00:00:00')
            const end = dateRange.endDate.format('YYYY-MM-DD 23:59:59')
            query += `&filter[createdAtRange]=${encodeURIComponent(`${start},${end}`)}`
        }

        this.setState({ loading: true })

        await API.get(`/api/orders/invoices?${query}`)
            .then(response => {
                const { payload } = response.data
                this.setState({
                    invoices: payload.data,
                    pagination: {
                        current: payload.current_page,
                        pageSize: payload.per_page,
                        total: payload.total,
                    },
                    loading: false,
                })
            })
            .catch(err => {
                console.error('Error al obtener facturas: ', err)
                this.setState({ loading: false })
            })
    }

    handleFilterChange = (name, value) => {
        this.setState({
            filters: {
                ...this.state.filters,
                [name]: value,
            },
        })
    }

    handleSearch = () => {
        this.getData({ page: 1 })
    }

    handleClearFilters = () => {
        this.setState(
            {
                filters: {
                    unique_id: '',
                    rfc: '',
                    socialReason: '',
                    subsidiary_id: null,
                },
                dateRange: {
                    startDate: null,
                    endDate: null,
                },
            },
            () => this.getData({ page: 1 })
        )
    }

    handleTableChange = pagination => {
        this.getData({ page: pagination.current })
    }

    handleViewInvoice = invoice => {
        this.setState({ selectedInvoice: invoice, isDetailModalOpen: true })
    }

    handleCloseDetailModal = () => {
        this.setState({ isDetailModalOpen: false })
    }

    handleViewPdf = order => {
        window.open(`/api/v2/orders/${order.id}/invoice`, '_blank')
    }

    handleDownloadXml = order => {
        API.get(`/api/v2/orders/${order.id}/invoice?xml=1`, { responseType: 'blob' })
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `factura-${order.unique_id}.xml`)
                document.body.appendChild(link)
                link.click()
                link.remove()
            })
            .catch(err => {
                console.error('Error al descargar XML: ', err)
            })
    }

    handleOpenResend = row => {
        const email = (row.customer && row.customer.billing_email) || row.customer_email || ''
        this.setState({ resend: { visible: true, order: row, email, sending: false } })
    }

    handleCloseResend = () => {
        this.setState({ resend: { visible: false, order: null, email: '', sending: false } })
    }

    handleResendEmailChange = e => {
        this.setState({ resend: { ...this.state.resend, email: e.target.value } })
    }

    handleSendResend = async () => {
        const { resend } = this.state
        const email = resend.email.trim()

        if (!email) {
            message.error('Captura un correo destino')
            return
        }

        this.setState({ resend: { ...resend, sending: true } })

        await API.post(`/api/orders/${resend.order.id}/resend-invoice-email`, { email })
            .then(response => {
                message.success(response.data.message || 'Factura reenviada correctamente')
                this.handleCloseResend()
            })
            .catch(err => {
                const errorMessage =
                    (err.response && err.response.data && err.response.data.message) ||
                    'Error al reenviar la factura'
                message.error(errorMessage)
                this.setState({ resend: { ...this.state.resend, sending: false } })
            })
    }

    render() {
        const { filters, dateRange, subsidiaries, invoices, pagination, loading, resend } = this.state

        const columns = [
            {
                title: 'Folio',
                dataIndex: 'unique_id',
                key: 'unique_id',
            },
            {
                title: 'Sucursal',
                key: 'subsidiary',
                render: (label, row) => (row.subsidiary ? row.subsidiary.name : '---'),
            },
            {
                title: 'Fecha',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (label, row) => (row.created_at ? moment(row.created_at).format('DD/MM/YYYY') : '---'),
            },
            {
                title: 'Razón social',
                key: 'social_reason',
                render: (label, row) => (row.customer ? row.customer.social_reason || '-SIN RAZÓN SOCIAL-' : '---'),
            },
            {
                title: 'RFC',
                key: 'rfc',
                render: (label, row) => (row.customer ? row.customer.rfc : '---'),
            },
            {
                title: 'Total',
                dataIndex: 'grand_total',
                key: 'grand_total',
                render: (label, row) => (
                    <NumberFormat
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator
                        prefix="$"
                        displayType="text"
                        value={row.grand_total}
                    />
                ),
            },
            {
                title: 'Acciones',
                key: 'actions',
                render: (label, row) => (
                    <>
                        <Tooltip title="Ver detalle">
                            <Button
                                style={{ marginRight: 8 }}
                                shape="circle"
                                icon="eye"
                                onClick={() => this.handleViewInvoice(row)}
                            />
                        </Tooltip>
                        <Tooltip title="Ver PDF">
                            <Button
                                style={{ marginRight: 8 }}
                                type="primary"
                                shape="circle"
                                icon="file-pdf"
                                onClick={() => this.handleViewPdf(row)}
                            />
                        </Tooltip>
                        <Tooltip title="Descargar XML">
                            <Button
                                style={{ marginRight: 8 }}
                                shape="circle"
                                icon="download"
                                onClick={() => this.handleDownloadXml(row)}
                            />
                        </Tooltip>
                        <Tooltip title="Reenviar por correo">
                            <Button shape="circle" icon="mail" onClick={() => this.handleOpenResend(row)} />
                        </Tooltip>
                    </>
                ),
            },
        ]

        return (
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: 3,
                    padding: '8px 16px 16px',
                }}
            >
                <Form>
                    <Row gutter={16}>
                        <Col lg={4}>
                            <Form.Item label="Folio">
                                <Input
                                    value={filters.unique_id}
                                    onChange={e => this.handleFilterChange('unique_id', e.target.value)}
                                    placeholder="N° de orden"
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={5}>
                            <Form.Item label="Sucursal">
                                <Select
                                    showSearch
                                    style={{ width: '100%' }}
                                    loading={subsidiaries.loading}
                                    placeholder="Todas"
                                    allowClear
                                    optionFilterProp="children"
                                    value={filters.subsidiary_id}
                                    onChange={value => this.handleFilterChange('subsidiary_id', value)}
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {subsidiaries.data.map(s => (
                                        <Option key={s.id} value={s.id}>
                                            {s.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col lg={5}>
                            <Form.Item label="RFC">
                                <Input
                                    value={filters.rfc}
                                    onChange={e => this.handleFilterChange('rfc', e.target.value)}
                                    placeholder="RFC exacto"
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={5}>
                            <Form.Item label="Razón social">
                                <Input
                                    value={filters.socialReason}
                                    onChange={e => this.handleFilterChange('socialReason', e.target.value)}
                                    placeholder="Razón social exacta"
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={5}>
                            <Form.Item label="Rango de fechas">
                                <RangePicker
                                    value={[dateRange.startDate, dateRange.endDate]}
                                    onChange={range => {
                                        this.setState({
                                            dateRange: {
                                                startDate: range[0],
                                                endDate: range[1],
                                            },
                                        })
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={2}>
                            <Button type="primary" disabled={loading} onClick={this.handleSearch}>
                                Buscar
                            </Button>
                        </Col>
                        <Col lg={3}>
                            <Button disabled={loading} onClick={this.handleClearFilters}>
                                Limpiar filtros
                            </Button>
                        </Col>
                    </Row>
                </Form>

                <Table
                    style={{ marginTop: 16 }}
                    dataSource={invoices}
                    columns={columns}
                    pagination={pagination}
                    rowKey="id"
                    onChange={this.handleTableChange}
                    loading={loading}
                />

                <InvoiceDetailModal
                    visible={this.state.isDetailModalOpen}
                    invoice={this.state.selectedInvoice}
                    onClose={this.handleCloseDetailModal}
                />

                <Modal
                    title={`Reenviar factura${resend.order ? ` - Orden #${resend.order.unique_id}` : ''}`}
                    visible={resend.visible}
                    onCancel={this.handleCloseResend}
                    onOk={this.handleSendResend}
                    okText="Enviar"
                    confirmLoading={resend.sending}
                >
                    <Form.Item label="Correo destino">
                        <Input
                            value={resend.email}
                            onChange={this.handleResendEmailChange}
                            placeholder="correo@ejemplo.com"
                        />
                    </Form.Item>
                </Modal>
            </div>
        )
    }
}

export default InvoicesListContainer
